import React, { useMemo, useState } from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, MessageCircle, TrendingUp, ShieldX } from 'lucide-react';
import { shuffle } from '../../utils/shuffle';
const DRIVER_DATA: Record<string, {
  label: string;
  score: number;
}[]> = {
  Nephrologist: [{
    label: 'Safety data',
    score: 90
  }, {
    label: 'Guidelines',
    score: 70
  }, {
    label: 'Outcome evidence',
    score: 60
  }, {
    label: 'Peer behavior',
    score: 40
  }],
  Cardiologist: [{
    label: 'Outcome evidence',
    score: 95
  }, {
    label: 'Guidelines',
    score: 60
  }, {
    label: 'Peer behavior',
    score: 50
  }, {
    label: 'Safety data',
    score: 40
  }],
  Endocrinologist: [{
    label: 'Guidelines',
    score: 85
  }, {
    label: 'Outcome evidence',
    score: 80
  }, {
    label: 'Safety data',
    score: 60
  }, {
    label: 'Peer behavior',
    score: 50
  }],
  PCP: [{
    label: 'Guidelines',
    score: 90
  }, {
    label: 'Peer behavior',
    score: 85
  }, {
    label: 'Safety data',
    score: 70
  }, {
    label: 'Outcome evidence',
    score: 50
  }]
};
const BARRIER_DATA: Record<string, {
  label: string;
  score: number;
}[]> = {
  Nephrologist: [{
    label: 'Late referrals',
    score: 90
  }, {
    label: 'Safety concerns',
    score: 85
  }, {
    label: 'Workload',
    score: 70
  }, {
    label: 'Lack of guidance',
    score: 40
  }],
  Cardiologist: [{
    label: 'Workload',
    score: 80
  }, {
    label: 'Lack of guidance',
    score: 50
  }, {
    label: 'Safety concerns',
    score: 30
  }, {
    label: 'Late referrals',
    score: 20
  }],
  Endocrinologist: [{
    label: 'Lack of guidance',
    score: 75
  }, {
    label: 'Workload',
    score: 65
  }, {
    label: 'Safety concerns',
    score: 60
  }, {
    label: 'Late referrals',
    score: 40
  }],
  PCP: [{
    label: 'Lack of guidance',
    score: 90
  }, {
    label: 'Workload',
    score: 85
  }, {
    label: 'Safety concerns',
    score: 70
  }, {
    label: 'Late referrals',
    score: 30
  }]
};
function RankingExercise({
  title,
  icon: IconComp,
  items,
  observedData,
  color,
  onComplete,
  isActive
}: {
  title: string;
  icon: React.ElementType;
  items: string[];
  observedData: {
    label: string;
    score: number;
  }[];
  color: string;
  onComplete: () => void;
  isActive: boolean;
}) {
  const [ranking, setRanking] = useState<string[]>([]);
  const [revealed, setRevealed] = useState(false);
  const handleItemClick = (item: string) => {
    if (revealed || ranking.includes(item)) return;
    const newRanking = [...ranking, item];
    setRanking(newRanking);
    if (newRanking.length === items.length) {
      setTimeout(() => {
        setRevealed(true);
        setTimeout(onComplete, 300);
      }, 400);
    }
  };
  const removeFromRanking = (item: string) => {
    if (revealed) return;
    setRanking(ranking.filter(r => r !== item));
  };
  const getRankPosition = (item: string) => ranking.indexOf(item);
  const getObservedRank = (item: string) => observedData.findIndex(d => d.label === item);
  return <div className={`transition-opacity duration-300 ${isActive ? 'opacity-100' : 'opacity-40 pointer-events-none'}`} data-id="element-126">
      <div className="flex items-center mb-5" data-id="element-127">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center mr-3" style={{
        backgroundColor: `${color}15`,
        color
      }} data-id="element-128">
          <IconComp size={22} data-id="element-129" />
        </div>
        <h3 className="text-xl font-bold text-dark" data-id="element-130">{title}</h3>
      </div>

      {!revealed ? <>
          <p className="text-sm text-gray-500 mb-4" data-id="element-131">
            Rank from most important (#1) to least (#4). Click in order.
          </p>

          {/* Ranking slots */}
          <div className="flex gap-2 mb-5 min-h-[44px]" data-id="element-132">
            {ranking.length > 0 ? ranking.map((item, i) => <motion.button key={item} initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} onClick={() => removeFromRanking(item)} className="flex items-center bg-white border-2 rounded-xl px-3 py-2 text-sm font-semibold hover:bg-red-50 hover:border-red-200 transition-colors" style={{
          borderColor: `${color}40`
        }} data-id="element-133">
                  <span className="w-5 h-5 rounded-full text-white text-xs flex items-center justify-center mr-2 flex-shrink-0" style={{
            backgroundColor: color
          }} data-id="element-134">
                    {i + 1}
                  </span>
                  {item}
                </motion.button>) : <div className="flex items-center text-gray-300 text-sm italic border-2 border-dashed border-gray-200 rounded-xl px-4 py-2 w-full" data-id="element-135">
                Click items below to rank them...
              </div>}
          </div>

          {/* Available items */}
          <div className="space-y-2" data-id="element-136">
            {items.map(item => {
          const isRanked = ranking.includes(item);
          return <motion.button key={item} whileHover={!isRanked ? {
            scale: 1.01
          } : {}} whileTap={!isRanked ? {
            scale: 0.99
          } : {}} onClick={() => handleItemClick(item)} className={`w-full p-4 rounded-xl border-2 text-left font-medium transition-all duration-200 ${isRanked ? 'opacity-30 bg-gray-50 border-gray-100 cursor-default' : 'bg-white border-gray-200 hover:border-gray-300 cursor-pointer'}`} data-id="element-137">
                  {item}
                </motion.button>;
        })}
          </div>
        </> : <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.4
    }} data-id="element-138">
          {/* Observed ranking visualization */}
          <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100" data-id="element-139">
            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-5" data-id="element-140">
              Observed Ranking
            </h4>
            <div className="space-y-4" data-id="element-141">
              {observedData.map((item, i) => {
            const userRank = getRankPosition(item.label);
            const rankDiff = userRank - i;
            return <motion.div key={item.label} initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: i * 0.15
            }} data-id="element-142">
                    <div className="flex items-center justify-between mb-1.5" data-id="element-143">
                      <div className="flex items-center" data-id="element-144">
                        <span className="w-7 h-7 rounded-lg text-white text-sm font-bold flex items-center justify-center mr-3" style={{
                    backgroundColor: color
                  }} data-id="element-145">
                          {i + 1}
                        </span>
                        <span className="font-semibold text-dark" data-id="element-146">
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-147">
                        {/* Show user's rank vs observed */}
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${rankDiff === 0 ? 'bg-green-100 text-green-700' : Math.abs(rankDiff) === 1 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`} data-id="element-148">
                          {rankDiff === 0 ? '✓ Correct' : `You said #${userRank + 1}`}
                        </span>
                        <span className="text-sm font-bold tabular-nums w-10 text-right" style={{
                    color
                  }} data-id="element-149">
                          {item.score}%
                        </span>
                      </div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded-full overflow-hidden ml-10" data-id="element-150">
                      <motion.div className="h-full rounded-full" style={{
                  backgroundColor: i === 0 ? color : `${color}${i === 1 ? 'AA' : i === 2 ? '77' : '44'}`
                }} initial={{
                  width: 0
                }} animate={{
                  width: `${item.score}%`
                }} transition={{
                  duration: 1,
                  delay: 0.3 + i * 0.15,
                  ease: 'easeOut'
                }} data-id="element-151" />
                    </div>
                  </motion.div>;
          })}
            </div>
          </div>
        </motion.div>}
    </div>;
}
export function DriversBarriers({
  state,
  updateState,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const [driversComplete, setDriversComplete] = useState(false);
  const [barriersComplete, setBarriersComplete] = useState(false);
  const driverItems = useMemo(() => shuffle(['Safety data', 'Outcome evidence', 'Guidelines', 'Peer behavior']), []);
  const barrierItems = useMemo(() => shuffle(['Safety concerns', 'Lack of guidance', 'Workload', 'Late referrals']), []);
  const observedDrivers = DRIVER_DATA[role];
  const observedBarriers = BARRIER_DATA[role];
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-152">
      <div className="max-w-4xl w-full mx-auto" data-id="element-153">
        <div className="text-center mb-10" data-id="element-154">
          <h2 className="text-3xl md:text-5xl font-bold text-dark" data-id="element-155">
            What drives and blocks {role}s?
          </h2>
          <p className="text-gray-500 mt-4 text-lg" data-id="element-156">
            As a group, rank each from most to least important — then see how
            your ranking compares.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10" data-id="element-157">
          <RankingExercise title="Rank Their Drivers" icon={TrendingUp} items={driverItems} observedData={observedDrivers} color={color} onComplete={() => setDriversComplete(true)} isActive={true} data-id="element-158" />
          <RankingExercise title="Rank Their Barriers" icon={ShieldX} items={barrierItems} observedData={observedBarriers} color={color} onComplete={() => setBarriersComplete(true)} isActive={driversComplete} data-id="element-159" />
        </div>

        <AnimatePresence data-id="element-160">
          {driversComplete && barriersComplete && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-161">
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-10 flex items-start max-w-2xl mx-auto" data-id="element-162">
                <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-163" />
                <div data-id="element-164">
                  <h4 className="text-blue-900 font-bold mb-2" data-id="element-165">
                    Discuss with your group:
                  </h4>
                  <ul className="text-blue-800 space-y-1.5" data-id="element-166">
                    <li data-id="element-167">
                      <strong data-id="element-168">Targeting:</strong> Which {role}s have the fewest
                      barriers to overcome?
                    </li>
                    <li data-id="element-169">
                      <strong data-id="element-170">Arguments:</strong> Which features and data points
                      address their top driver?
                    </li>
                    <li data-id="element-171">
                      <strong data-id="element-172">Motivation:</strong> How do you turn their primary
                      driver into a reason to act now?
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-center pb-8" data-id="element-173">
                <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-174">
                  See the Reality Check
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-175" />
                </motion.button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}